/**
 * @file: linking.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import { config } from '@euler/config';
import {
  getInitialPath,
  setInitialPath,
} from '@euler/functions/navigation/linking/initial-url';
import { safeMarkDevFlag } from '@euler/utils';
import {
  LinkingOptions,
  NavigationState,
  PartialRoute,
  PartialState,
  Route,
  StackActions,
  getActionFromState,
  getPathFromState,
  getStateFromPath,
} from '@react-navigation/native';
import { findLastIndex, update } from 'ramda';

export const kLinkingLogEnabled = safeMarkDevFlag(true);

const fixPath = (path: string) => {
  return path.replace(/^\/Root/, '').replace(/^\/Drawer/, '');
};

// refer to the following documentation on react-navigation for how to enable
// deep linking and universal links and how to configure links:
// - https://reactnavigation.org/docs/deep-linking/
// - https://reactnavigation.org/docs/configuring-links
export function linkingFactory<T extends Record<string, any>>(
  configuration: LinkingOptions<T>['config'],
  factoryOptions?: {
    /**
     * rewrites an incoming path before it is resolved against `configuration`,
     * e.g. to map a path that only exists in another config onto its
     * equivalent here (see `rewriteAuthedPath`).
     */
    rewritePath?: (path: string) => string;
    /**
     * reshapes the state resolved from a path, e.g. to seed the pages a
     * deep-linked page sits on (see `withAiHistory`).
     */
    rewriteState?: (
      state: ReturnType<typeof getStateFromPath>,
    ) => ReturnType<typeof getStateFromPath>;
    /**
     * a link opened in the running app pushes its page on top of the
     * user's current pages (see `openOnTop`); the root navigator must be a
     * stack.
     */
    openLinksOnTop?: boolean;
  },
): LinkingOptions<T> {
  return {
    prefixes: [config.linking.url, `${config.linking.scheme}://`],
    getPathFromState(state, options) {
      const cleanedState = navigationStateCleanedUp(state);
      const path = cleanedState ? getPathFromState(cleanedState, options) : '/';
      const segments = path.split('/');
      const invalidIndex = segments.findIndex(c => c === 'undefined');

      let res = '';
      if (invalidIndex >= 0) {
        res = fixPath(segments.slice(0, invalidIndex).join('/'));
      } else {
        res = fixPath(path);
      }

      if (kLinkingLogEnabled) {
        console.log('[linking] path for state:', res, state, options);
      }

      return res;
    },
    getStateFromPath(path, options) {
      if (path.includes('/wechat/')) return undefined;

      // you can add any rewrites of the path here.
      const rewritten = factoryOptions?.rewritePath?.(path) ?? path;
      const resolved = getStateFromPath(rewritten, options);
      const state = factoryOptions?.rewriteState?.(resolved) ?? resolved;

      if (!getInitialPath()) {
        // save the initial path for future use where necessary.
        setInitialPath(path);
      }

      kLinkingLogEnabled &&
        console.log(
          '[linking] state for path: ',
          rewritten,
          ': ',
          JSON.stringify(state, null, 2),
          ', url: ',
          config.linking.url,
        );
      return state;
    },
    ...(factoryOptions?.openLinksOnTop
      ? { getActionFromState: openOnTop }
      : {}),
    config: configuration,
  };
}

type ActionFromState = typeof getActionFromState;

/**
 * The action for a link opened in the running app (React Navigation
 * resolves the URL the app starts on to a state, and every later link to
 * an action). A leaf page of the root stack is pushed on top of the user's
 * current pages — alone, without the history `rewriteState` seeds beneath
 * it, and even when a page of the same name is open, which a navigate
 * would go back to or swap the params of. A page inside a nested
 * navigator navigates as before.
 */
function openOnTop(
  state: Parameters<ActionFromState>[0],
  options?: Parameters<ActionFromState>[1],
): ReturnType<ActionFromState> {
  const routes = state.routes.slice(0, (state.index ?? Infinity) + 1);
  const target = routes[routes.length - 1];
  const screen = (options?.screens as Record<string, unknown> | undefined)?.[
    target?.name ?? ''
  ];
  const nested = typeof screen === 'object' && screen && 'screens' in screen;
  if (!target || target.state || nested) {
    return getActionFromState(state, options);
  }
  // The linking type admits only the common actions; the root stack
  // handles a push.
  return StackActions.push(
    target.name,
    target.params,
  ) as unknown as ReturnType<ActionFromState>;
}

/**
 * A helper function that clean the given navigation state by removing all
 * transient (temporary or private) routes whose keys start with "_".
 * @param state the navigation state to cleanup
 * @returns the transformed/cleaned navigation state
 */
function navigationStateCleanedUp(
  state: NavigationState | Omit<PartialState<NavigationState>, 'stale'>,
): typeof state | null {
  const lastViewableRouteIdx = findLastIndex(
    route => !route.name.startsWith('_'),
    state.routes.slice(0, (state.index ?? 0) + 1) as PartialRoute<
      Route<string, object | undefined>
    >[],
  );

  if (lastViewableRouteIdx < 0) return null;

  const lastViewableRoute = state.routes[lastViewableRouteIdx];
  let newState = lastViewableRoute.state;

  if (newState) {
    newState = navigationStateCleanedUp(newState) ?? undefined;
  }

  return {
    ...state,
    routes: update(
      lastViewableRouteIdx,
      {
        ...lastViewableRoute,
        state: newState,
      },
      state.routes as any,
    ) as any,
    index: lastViewableRouteIdx,
  };
}
